import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface DiagramInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedSolution?: string;
  selectedSolutionDescription?: string;
  selectedTaxonomy?: string;
  selectedTaxonomyDescription?: string;
  selectedSubprocess?: string;

  type?: 'solution' | 'taxonomy';
}

const DiagramInfoModal: React.FC<DiagramInfoModalProps> = ({
  isOpen,
  onClose,
  selectedSolution,
  selectedSolutionDescription,
  selectedTaxonomy,
  selectedTaxonomyDescription,

  type = 'solution',
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [displayData, setDisplayData] = useState({
    name: '',
    description: '',
    type: '',
  });

  useEffect(() => {
    let name = '';
    let description = '';

    if (type === 'taxonomy' && selectedTaxonomy) {
      name = selectedTaxonomy || 'selectedTaxonomyDescription Taxonomy';
      description = selectedTaxonomyDescription || 'No description available for this taxonomy';
    } else if (type === 'solution' && selectedSolution) {
      name = selectedSolution || 'Unnamed Solution';
      description = selectedSolutionDescription || 'No description available for this solution';
    }

    setDisplayData({
      name: name || 'No name provided',
      description: description || 'No description provided',
      type,
    });
  }, [
    selectedSolution,
    selectedSolutionDescription,
    selectedTaxonomy,
    selectedTaxonomyDescription,
    type,
  ]);

  // Handle clicking outside the modal to close it
  const handleBackdropClick = () => {
    onClose();
  };

  const getModalTitle = () => {
    return 'Diagram Info';
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleBackdropClick}
      onClick={handleBackdropClick}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: isMobile ? '16px 16px 0 0' : 2,
          minHeight: isMobile ? '50vh' : '300px',
          maxHeight: isMobile ? '50vh' : '500px',
          margin: isMobile ? 0 : undefined,
          ...(isMobile && {
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            width: '100vw',
            height: '50vh',
            top: 'auto',
            transform: 'none',
            borderRadius: '16px 16px 0 0',
          }),
        },
        onClick: (e: { stopPropagation: () => void }) => e.stopPropagation(),
      }}
      sx={{
        ...(isMobile && {
          '& .MuiDialog-container': {
            alignItems: 'flex-end',
          },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
        }),
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          ...(isMobile && {
            position: 'sticky',
            top: 0,
            backgroundColor: 'white',
            zIndex: 1,
            borderBottom: '1px solid #e0e0e0',
            pt: 1,
            pb: 1,
          }),
        }}
      >
        <Typography variant="h5" sx={{ color: '#4285f4', fontWeight: 'bold' }}>
          {getModalTitle()}
        </Typography>
        {isMobile && (
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent
        sx={{
          pb: 3,
          ...(isMobile && {
            flex: 1,
            overflow: 'auto',
            maxHeight: 'calc(50vh - 80px)',
          }),
        }}
      >
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#333' }}>
            Name
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mb: 3,
              p: 2,
              backgroundColor: '#f8f9fa',
              borderRadius: 1,
              border: '1px solid #e9ecef',
              minHeight: '24px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {displayData.name}
          </Typography>

          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#333' }}>
            Description
          </Typography>
          <Typography
            variant="body1"
            sx={{
              p: 2,
              backgroundColor: '#f8f9fa',
              borderRadius: 1,
              border: '1px solid #e9ecef',
              minHeight: '120px',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {displayData.description}
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default DiagramInfoModal;
